import { useEffect, useRef } from 'react'

import { useDigitalProductContext } from '@/shared/contexts/DigitalProductContext'
import { useUserProgress } from '@/shared/hooks/useUserProgress'
import { useGamificationDataStore } from '@/shared/store/gamificationDataStore'
import type {
  CsatCampaign,
  CsatResourceType,
  CsatSubmission
} from '@/types/csat'

import { decideForCompletion, decideFinal } from './decide'
import { useCsatStore } from './store'
import { today } from './api'

const SUPPORTED_TYPES: CsatResourceType[] = [
  'Partner',
  'Programming',
  'Level',
  'Mission',
  'Quiz'
]

// Watcher de CSAT: observa conclusões de recurso (via diffs do progress) e o
// app_open, decidindo localmente quando abrir um prompt. A lógica pura de
// decisão vive em `decide.ts`; este hook apenas conecta dados e estado.
export const useCsatWatcher = () => {
  const { digitalProductId } = useDigitalProductContext()
  const { userProgress } = useUserProgress()
  // Assina a fatia de dados do produto ativo para re-renderizar quando o
  // `gamification data` (e as campanhas CSAT) for atualizado.
  const data = useGamificationDataStore(state =>
    state.activeDigitalProductId
      ? state.dataByProductId[state.activeDigitalProductId]
      : undefined
  )
  const { activePrompt, setActivePrompt, isDismissed } = useCsatStore()

  const campaigns: CsatCampaign[] = data?.csat_campaigns ?? []
  const submissions: CsatSubmission[] = userProgress?.csat_submissions ?? []

  // Snapshot da última leitura de progresso para detectar novas conclusões.
  const prevFinishedRef = useRef<Record<CsatResourceType, Set<number>> | null>(
    null
  )

  const completedIdsByType = (): Record<CsatResourceType, Set<number>> => ({
    Partner: new Set(userProgress?.finished_partners ?? []),
    Programming: new Set(userProgress?.finished_programmings ?? []),
    Level: new Set(userProgress?.finished_levels ?? []),
    Mission: new Set(userProgress?.finished_missions ?? []),
    Quiz: new Set((userProgress?.quiz_progress ?? []).map(p => p.quiz_id))
  })

  // Detecta novas conclusões e tenta resolver interaction/topic.
  useEffect(() => {
    if (!userProgress) return

    const current = completedIdsByType()

    // Primeira leitura: apenas registra a baseline, sem disparar prompts.
    if (!prevFinishedRef.current) {
      prevFinishedRef.current = current
      return
    }

    if (activePrompt) {
      prevFinishedRef.current = current
      return
    }

    const prev = prevFinishedRef.current
    for (const type of SUPPORTED_TYPES) {
      const newlyCompleted = Array.from(current[type]).filter(
        id => !prev[type].has(id)
      )
      for (const id of newlyCompleted) {
        const prompt = decideForCompletion({
          campaigns,
          submissions,
          type,
          id,
          isDismissed
        })
        if (prompt) {
          setActivePrompt(prompt)
          prevFinishedRef.current = current
          return
        }
      }
    }

    prevFinishedRef.current = current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProgress])

  // app_open: avalia final ao montar (e quando dados/progresso chegam).
  useEffect(() => {
    if (!userProgress || activePrompt) return

    const prompt = decideFinal({
      campaigns,
      submissions,
      today: today(),
      isDismissed
    })
    if (prompt) setActivePrompt(prompt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digitalProductId, userProgress?.id, campaigns.length])
}
